import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchApplications as fetchApplicationsService } from '@/domains/application/applicationApi';
import type { Application } from '@/types';

interface ApplicationsState {
    applications: Application[];
    isLoading: boolean;
    error: Error | null;
    lastFetched: number | null;
}

const CACHE_TIME = 5 * 60 * 1000;

const useApplicationsStore = create<
    ApplicationsState & {
        fetchApplications: (force?: boolean) => Promise<void>;
        clearCache: () => void;
    }
>()(
    persist(
        (set, get) => ({
            applications: [],
            isLoading: false,
            error: null,
            lastFetched: null,

            fetchApplications: async (force = false) => {
                const { lastFetched, applications } = get();
                const now = Date.now();

                if (!force && lastFetched && now - lastFetched < CACHE_TIME && applications.length > 0) {
                    return;
                }

                set({ isLoading: true, error: null });
                try {
                    const response = await fetchApplicationsService();
                    set({
                        applications: response.data,
                        isLoading: false,
                        lastFetched: now,
                    });
                } catch (error) {
                    set({ error: error as Error, isLoading: false });
                }
            },

            clearCache: () =>
                set({ applications: [], lastFetched: null, error: null }),
        }),
        {
            name: 'applications-storage',
            partialize: (state) => ({
                applications: state.applications,
                lastFetched: state.lastFetched,
            }),
        },
    ),
);

export default useApplicationsStore;
